import { useReducer } from "react";
import { Link } from "wouter";
import { DateTime } from "luxon";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import {
  QuoteTrackingStateSchema,
  quoteTrackingLabels,
  type ListQuoteTrackingInput,
  type QuoteTrackingSortColumn,
} from "cadendar-shared";
import { useQuoteTracking } from "./useQuotes";
import { usePreferences } from "../main/state/globalState";
import { openPatientFile } from "../patients/openPatientFile";
import QuoteTrackingControl from "./QuoteTrackingControl";

type FilterAction =
  | { type: "filter"; value: Partial<Omit<ListQuoteTrackingInput, "page">> }
  | { type: "page"; value: number }
  | { type: "sort"; column: QuoteTrackingSortColumn };

function filterReducer(filters: ListQuoteTrackingInput, action: FilterAction) {
  if (action.type === "sort") {
    return {
      ...filters,
      sortBy: action.column,
      sortDirection:
        filters.sortBy === action.column && filters.sortDirection === "asc"
          ? "desc"
          : "asc",
      page: 0,
    } satisfies ListQuoteTrackingInput;
  }
  return action.type === "page"
    ? { ...filters, page: action.value }
    : { ...filters, ...action.value, page: 0 };
}

export default function QuoteTrackingPage() {
  const preferences = usePreferences();
  const [filters, dispatch] = useReducer(filterReducer, {
    search: "",
    sort: "oldest",
    sortBy: "creationDate",
    sortDirection: "asc",
    page: 0,
  });
  const invalidPeriod = Boolean(
    filters.from && filters.to && filters.from > filters.to,
  );
  const query = useQuoteTracking(filters, {
    enabled: !invalidPeriod,
    refetchInterval: 30_000,
  });
  const result = invalidPeriod ? undefined : query.data;
  const stats = result?.stats;
  const filter = (value: Partial<Omit<ListQuoteTrackingInput, "page">>) =>
    dispatch({ type: "filter", value });

  return (
    <main className="mx-auto max-w-7xl space-y-5 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Suivi des devis</h1>
        <Link href="/" className="text-blue-700 underline">
          Retour à l’agenda
        </Link>
      </header>
      <section
        aria-label="Filtres des devis"
        className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4"
      >
        <label className="text-sm">
          Patient ou devis
          <input
            type="search"
            maxLength={200}
            className="mt-1 block rounded border p-2"
            value={filters.search}
            onChange={(event) => filter({ search: event.target.value })}
            placeholder="Nom, prénom, intitulé…"
          />
        </label>
        <label className="text-sm">
          Statut
          <select
            className="mt-1 block rounded border p-2"
            value={filters.state ?? ""}
            onChange={(event) =>
              filter({
                state: event.target.value
                  ? QuoteTrackingStateSchema.parse(event.target.value)
                  : undefined,
              })
            }
          >
            <option value="">Tous les statuts</option>
            {QuoteTrackingStateSchema.options.map((state) => (
              <option key={state} value={state}>
                {quoteTrackingLabels[state]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Remis à partir du
          <input
            type="date"
            className="mt-1 block rounded border p-2"
            value={filters.from ?? ""}
            onChange={(event) =>
              filter({ from: event.target.value || undefined })
            }
          />
        </label>
        <label className="text-sm">
          Remis jusqu’au
          <input
            type="date"
            className="mt-1 block rounded border p-2"
            value={filters.to ?? ""}
            onChange={(event) =>
              filter({ to: event.target.value || undefined })
            }
          />
        </label>
      </section>
      {invalidPeriod && (
        <p role="alert" className="text-red-700">
          La date de début doit précéder la date de fin.
        </p>
      )}
      {stats && (
        <section aria-label="Statistiques des devis" className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Taux d’acceptation",
                value:
                  stats.acceptanceRate === null
                    ? "—"
                    : (stats.acceptanceRate * 100).toLocaleString("fr-FR", {
                        maximumFractionDigits: 1,
                      }) + " %",
              },
              {
                label: "Acceptés / remis valides",
                value: `${stats.accepted} / ${stats.delivered}`,
              },
              {
                label: "Abandonnés après acceptation",
                value: stats.abandonedAfterAcceptance,
              },
              {
                label: "Réalisés sans acceptation (statut)",
                value: stats.counts.performed_without_acceptance,
              },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="rounded-lg border border-slate-200 bg-white p-4"
              >
                <p className="text-sm text-slate-600">{label}</p>
                <p className="mt-1 text-2xl font-semibold">{value}</p>
              </div>
            ))}
          </div>
          {stats.acceptedFromRealization > 0 && (
            <p className="text-sm text-slate-600">
              Dont {stats.acceptedFromRealization} devis compté(s) comme
              accepté(s) car le traitement est commencé, sans acceptation
              enregistrée.
            </p>
          )}
          <div
            className="flex flex-wrap gap-3 text-sm text-slate-600"
            aria-label="Statuts enregistrés"
          >
            <span className="font-medium">Statuts enregistrés :</span>
            {QuoteTrackingStateSchema.options
              .filter((state) => state !== "invalid")
              .map((state) => (
                <span key={state}>
                  {quoteTrackingLabels[state]} : {stats.counts[state]}
                </span>
              ))}
          </div>
          {result?.realizationStats && (
            <div
              className="flex flex-wrap gap-3 text-sm text-slate-600"
              aria-label="Réalisation automatique des devis"
            >
              <span>
                Réalisés automatiquement : {result.realizationStats.performed}
              </span>
              <span>
                Partiellement réalisés : {result.realizationStats.partial}
              </span>
              <span>
                Correspondances ambiguës : {result.realizationStats.ambiguous}
              </span>
              <span>
                Réalisés sans acceptation enregistrée (détection) :{" "}
                {result.realizationStats.withoutRecordedAcceptance}
              </span>
            </div>
          )}
          <p className="text-xs text-slate-500">
            Statistiques sur tous les résultats filtrés, pas seulement cette
            page. Taux = devis acceptés ou avec au moins un acte réalisé
            retrouvé / devis remis valides avec date de remise connue. Les
            réalisations partielles et complètes comptent comme acceptations
            statistiques, sans modifier le statut manuel. Les devis créés et
            invalides sont exclus du taux. Une acceptation suivie d’un abandon
            reste comptée. Les périodes portent sur la date de remise, pas sur
            la date d’acceptation.
          </p>
          {stats.excludedInvalid > 0 && (
            <p className="text-sm text-slate-600">
              {stats.excludedInvalid} devis invalide(s) affiché(s), exclu(s) des
              statistiques.
            </p>
          )}
          {stats.missingDeliveryDate > 0 && (
            <p className="text-sm text-amber-800">
              {stats.missingDeliveryDate} devis sans date de remise : à
              compléter pour les inclure dans le taux.
            </p>
          )}
          {(filters.from || filters.to) && (
            <p className="text-xs text-slate-500">
              Les devis sans date de remise ne figurent pas dans une période
              datée.
            </p>
          )}
        </section>
      )}
      {!invalidPeriod && query.isPending && (
        <p role="status">Chargement des devis…</p>
      )}
      {!!query.error && !invalidPeriod && (
        <p role="alert" className="text-red-700">
          Impossible de charger le suivi des devis.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => query.refetch()}
          >
            Réessayer
          </button>
        </p>
      )}
      {result && (
        <>
          <p className="text-sm text-slate-600">
            {result.total} devis trouvé(s)
          </p>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-100">
                <tr>
                  {(
                    [
                      { column: "patient", label: "Patient", className: "" },
                      { column: "name", label: "Devis", className: "" },
                      {
                        column: "creationDate",
                        label: "Création",
                        className: "",
                      },
                      {
                        column: "totalFees",
                        label: "Total devis",
                        className: "text-right",
                      },
                      {
                        column: "totalPerformedFees",
                        label: "Total réalisé",
                        className: "text-right",
                      },
                      {
                        column: "state",
                        label: "Suivi",
                        className: "min-w-72",
                      },
                    ] satisfies Array<{
                      column: QuoteTrackingSortColumn;
                      label: string;
                      className: string;
                    }>
                  ).map(({ column, label, className }) => {
                    const active = filters.sortBy === column;
                    return (
                      <th
                        key={column}
                        scope="col"
                        className={`p-3 ${className}`}
                        aria-sort={
                          active
                            ? filters.sortDirection === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                      >
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 hover:text-blue-700"
                          aria-label={`${label}, ${active ? `tri ${filters.sortDirection === "asc" ? "croissant" : "décroissant"}` : "tri disponible"}`}
                          onClick={() => dispatch({ type: "sort", column })}
                        >
                          {label}
                          {active && (
                            <span aria-hidden="true">
                              {filters.sortDirection === "asc" ? (
                                <FaArrowUp />
                              ) : (
                                <FaArrowDown />
                              )}
                            </span>
                          )}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((quote) => (
                  <tr
                    key={quote.id}
                    className="border-t border-slate-200 align-top"
                  >
                    <td className="p-3">
                      <button
                        type="button"
                        className="cursor-pointer text-left font-medium text-blue-700 underline"
                        onClick={() =>
                          openPatientFile({
                            patientId: quote.patientId,
                            allowSingleWindow:
                              preferences?.single_patient_file_window ?? true,
                          })
                        }
                      >
                        {quote.patientName}
                      </button>
                    </td>
                    <td className="p-3">{quote.name}</td>
                    <td className="whitespace-nowrap p-3">
                      {DateTime.fromJSDate(quote.creationDate).toFormat(
                        "dd/MM/yyyy",
                      )}
                    </td>
                    <td className="whitespace-nowrap p-3 text-right">
                      {Number(quote.totalFees).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </td>
                    <td className="whitespace-nowrap p-3 text-right">
                      {quote.realization?.totalPerformedFees !== undefined
                        ? Number(
                            quote.realization.totalPerformedFees,
                          ).toLocaleString("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                          })
                        : "—"}
                    </td>
                    <td className="p-3">
                      <QuoteTrackingControl
                        quoteId={quote.id}
                        tracking={quote.tracking}
                        realization={quote.realization}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {result.total === 0 && (
              <p className="p-6 text-center text-slate-500">
                Aucun devis ne correspond aux filtres.
              </p>
            )}
            {result.total > 0 && result.rows.length === 0 && (
              <p className="p-6 text-center text-slate-500">
                Plus de devis sur cette page. Revenez à la page précédente.
              </p>
            )}
          </div>
          <nav
            aria-label="Pagination des devis"
            className="flex items-center justify-between gap-3"
          >
            <button
              type="button"
              className="rounded border px-3 py-2 disabled:opacity-40"
              disabled={filters.page === 0 || query.isFetching}
              onClick={() =>
                dispatch({ type: "page", value: Math.max(0, filters.page - 1) })
              }
            >
              Précédent
            </button>
            <span className="text-sm">
              Page {filters.page + 1} /{" "}
              {Math.max(1, Math.ceil(result.total / result.pageSize))}
            </span>
            <button
              type="button"
              className="rounded border px-3 py-2 disabled:opacity-40"
              disabled={
                (filters.page + 1) * result.pageSize >= result.total ||
                query.isFetching
              }
              onClick={() =>
                dispatch({ type: "page", value: filters.page + 1 })
              }
            >
              Suivant
            </button>
          </nav>
        </>
      )}
    </main>
  );
}
